<script>
  // @ts-nocheck
  import { onDestroy, onMount } from "svelte";
  import { api, downloadProfileExport, installLearningBridge, setCsrf } from "./api.js";

  let phase = "loading";
  let sessionEnvelope = null;
  let profiles = [];
  let overview = null;
  let selectedProfile = null;
  let error = "";
  let notice = "";
  let busy = false;
  let authMode = "login";
  let authToken = "";
  let gameAvailable = false;
  let gameBuildUrl = "";
  let gameInstanceUrl = "";
  let gameStatus = "loading";
  let gameFrame = null;
  let isOnline = navigator.onLine;
  let learningBridge = null;
  let devices = [];
  let deletingProfile = null;
  let profileDeletePending = false;
  let deleteConfirmation = "";
  let deletePassword = "";
  let deleteDialog = null;
  let deleteDialogError = "";
  let editingProfile = null;
  let profileSavePending = false;
  let profileDialog = null;
  let profileDialogError = "";
  let deletingAccount = false;
  let accountDeletePending = false;
  let accountDeleteConfirmation = "";
  let accountDeletePassword = "";
  let accountDeleteDialog = null;
  let accountDeleteDialogError = "";
  let restoreFocus = null;
  let publicPage = "";
  let syncStatus = "offline_ready";
  let loaderTimer = 0;
  let runtimeTimer = 0;
  let loaderProgress = 0;
  let activatingProfile = false;
  let activationGeneration = 0;

  const updateConnection = () => { isOnline = navigator.onLine; };

  function gateGameFrame(node, status) {
    const apply = (nextStatus) => {
      if (nextStatus === "ready") {
        node.removeAttribute("inert");
        node.removeAttribute("tabindex");
      } else {
        node.setAttribute("inert", "");
        node.setAttribute("tabindex", "-1");
      }
    };
    apply(status);
    return { update: apply };
  }

  function oauthReturnPath() {
    const value = new URLSearchParams(window.location.search).get("return_to") || "";
    const isAuthorization = value.startsWith("/oauth/authorize?") || value.startsWith("/native/reauth/authorize?");
    return isAuthorization && !value.startsWith("//") ? value : "";
  }

  function consumeAuthToken() {
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const query = new URLSearchParams(window.location.search);
    const token = fragment.get("token") || query.get("token") || "";
    query.delete("token");
    query.delete("invitation");
    const suffix = query.toString();
    if (token || window.location.hash) window.history.replaceState({}, "", window.location.pathname + (suffix ? `?${suffix}` : ""));
    return token;
  }

  function legalHref(path) {
    const returnPath = oauthReturnPath();
    return returnPath ? `${path}?return_to=${encodeURIComponent(returnPath)}` : path;
  }

  function publicReturnHref() {
    const value = new URLSearchParams(window.location.search).get("return_to") || "";
    const isAuthorization = value.startsWith("/oauth/authorize?") || value.startsWith("/native/reauth/authorize?");
    return isAuthorization && !value.startsWith("//") ? `/?return_to=${encodeURIComponent(value)}` : "/";
  }

  function externalContactLinksAllowed() {
    return !oauthReturnPath();
  }

  function requestedAccountAction() {
    return new URLSearchParams(window.location.search).get("account") === "delete" ? "delete" : "";
  }

  function openRequestedAccountAction() {
    if (phase !== "adult" || requestedAccountAction() !== "delete") return;
    window.history.replaceState({}, "", "/");
    askToDeleteAccount();
  }

  function clearGameTimers() {
    window.clearTimeout(loaderTimer);
    window.clearTimeout(runtimeTimer);
    loaderTimer = 0;
    runtimeTimer = 0;
  }

  async function discoverGameBuild(timeoutMs = 10_000) {
    gameStatus = "loading";
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch("/game/build.json", { cache: "no-store", signal: controller.signal });
      if (!response.ok) throw new Error("manifest unavailable");
      const manifest = await response.json();
      const expectedEntry = `/game/builds/${manifest.build_id}/index.html`;
      if (manifest.schema_version !== "wissensbande.web-build.v1" ||
          manifest.bridge_protocol !== "wissensbande.host.v2" ||
          !/^[a-f0-9]{16,64}$/.test(manifest.build_id) ||
          manifest.entrypoint !== expectedEntry) {
        throw new Error("manifest invalid");
      }
      gameBuildUrl = expectedEntry;
      gameAvailable = true;
      gameStatus = "available";
    } catch {
      gameAvailable = false;
      gameBuildUrl = "";
      gameStatus = "unavailable";
    } finally {
      window.clearTimeout(timeout);
    }
  }

  const profileColour = (key) => key === "johanna" ? "#6552b8" : key === "philip" ? "#256aa8" : "#1f9f93";
  const avatar = (profile) => "/" + String(profile.avatar_key || "characters/philip").replace(/^\//, "") + (String(profile.avatar_key || "").endsWith(".svg") ? "" : ".svg");
  const rankName = (value) => ({
    crew_rookie: "Bandenneuling",
    trail_scout: "Pfadfinder",
    code_breaker: "Codeknacker",
    world_pro: "Weltenprofi",
    knowledge_hero: "Wissensheld",
    sky_navigator: "Himmelsnavigator",
    mission_captain: "Missionskapitän",
    galaxy_scout: "Galaxiescout",
    knowledge_commander: "Wissens-Captain",
    semester_legend: "Semesterlegende"
  })[value] || "Bandenneuling";

  async function initialize() {
    if (["/datenschutz", "/impressum", "/support"].includes(window.location.pathname)) {
      publicPage = window.location.pathname.slice(1);
      phase = "public";
      return;
    }
    const query = new URLSearchParams(window.location.search);
    authToken = consumeAuthToken();
    if (window.location.pathname === "/konto-bestaetigen" && authToken) {
      await discoverGameBuild();
      await verifySignupToken();
      return;
    } else if (window.location.pathname === "/passwort-zuruecksetzen" && authToken) {
      authMode = "reset-complete";
    } else if ((window.location.pathname === "/einladung" || query.has("invitation")) && authToken) {
      authMode = "invitation";
    }
    learningBridge = installLearningBridge(() => sessionEnvelope, closeProfile, handleLearningEvent);
    window.addEventListener("online", updateConnection);
    window.addEventListener("offline", updateConnection);
    window.addEventListener("message", handleLoaderMessage);
    await discoverGameBuild();
    await bootstrap();
  }

  onMount(() => { void initialize(); });

  onDestroy(() => {
    clearGameTimers();
    window.removeEventListener("online", updateConnection);
    window.removeEventListener("offline", updateConnection);
    window.removeEventListener("message", handleLoaderMessage);
    learningBridge?.destroy();
  });

  function handleLoaderMessage(event) {
    if (!gameFrame || event.source !== gameFrame.contentWindow || event.origin !== window.location.origin) return;
    if (event.data?.type === "wissensbande.loader-progress") {
      gameStatus = "loading";
      const total = Number(event.data.total || 0);
      loaderProgress = total > 0 ? Math.max(0, Math.min(100, Math.round(Number(event.data.current || 0) * 100 / total))) : 0;
    }
    if (event.data?.type === "wissensbande.runtime-initializing") {
      window.clearTimeout(loaderTimer);
      gameStatus = "initializing";
      runtimeTimer = window.setTimeout(() => { gameStatus = "runtime-timeout"; }, 30_000);
    }
    if (event.data?.type === "wissensbande.loader-error") {
      window.clearTimeout(loaderTimer);
      window.clearTimeout(runtimeTimer);
      gameStatus = "loader-error";
    }
  }

  async function bootstrap() {
    phase = "loading";
    error = "";
    try {
      sessionEnvelope = await api("/api/v1/session");
      setCsrf(sessionEnvelope.csrf_token);
      await loadProfiles();
      await loadCommerce();
      if (sessionEnvelope.session.kind === "adult") {
		const returnPath = oauthReturnPath();
		if (returnPath) {
		  window.location.assign(returnPath);
		  return;
		}
        await loadOverview();
      }
      phase = sessionEnvelope.session.kind;
      openRequestedAccountAction();
    } catch (requestError) {
      if (requestError.status === 401) {
        sessionEnvelope = null;
        setCsrf("");
        phase = "login";
      } else {
        error = requestError.message;
        phase = "offline";
      }
    }
  }

  async function loadProfiles() {
    const result = await api("/api/v1/profiles");
    profiles = result.profiles;
  }

  async function loadCommerce() {
	try {
	  const commerce = await api("/api/v1/commerce/bootstrap");
	  sessionEnvelope = { ...sessionEnvelope, commerce };
	} catch {
	  // Starter activities remain available; paid content fails closed in Godot.
	  sessionEnvelope = { ...sessionEnvelope, commerce: null };
	}
  }

  async function handleLearningEvent(event) {
    if (event.type === "runtime-ready") {
      window.clearTimeout(runtimeTimer);
      gameStatus = "ready";
    }
    if (event.type === "connection") {
      isOnline = event.online !== false;
      syncStatus = event.status || (isOnline ? "offline_ready" : "waiting_for_connection");
      if (event.status === "authentication_required") {
        activationGeneration += 1;
        clearGameTimers();
        learningBridge?.deactivate();
        selectedProfile = null;
        gameInstanceUrl = "";
        sessionEnvelope = null;
        profiles = [];
        setCsrf("");
        error = "Deine Anmeldung ist abgelaufen. Bitte melde dich erneut an.";
        phase = "login";
        return;
      }
    }
    if (event.type === "safe-error") {
      error = "Die Lernmission konnte nicht sicher fortgesetzt werden. Bitte öffne sie erneut.";
      gameStatus = "safe-error";
    }
    if (event.type === "progress-synced" || event.type === "sync-settled") {
      try {
        await loadProfiles();
        selectedProfile = profiles.find((profile) => profile.id === selectedProfile?.id) || selectedProfile;
      } catch {
        // The canvas retains durable local progress and will retry synchronization.
      }
    }
  }

  async function loadOverview() {
    overview = await api("/api/v1/parent/overview");
    const result = await api("/api/v1/parent/devices");
    devices = result.devices;
  }

  async function login(event) {
    busy = true;
    error = "";
    const form = new FormData(event.currentTarget);
    try {
      sessionEnvelope = await api("/api/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({
          email: form.get("email"),
          password: form.get("password")
        })
      });
      setCsrf(sessionEnvelope.csrf_token);
	  const returnPath = oauthReturnPath();
	  if (returnPath) {
		window.location.assign(returnPath);
		return;
	  }
      await loadProfiles();
      await loadCommerce();
      await loadOverview();
      phase = "adult";
      openRequestedAccountAction();
    } catch (requestError) {
      error = requestError.message;
    } finally {
      busy = false;
    }
  }

  async function signup(event) {
	const form = new FormData(event.currentTarget);
	if (form.get("password") !== form.get("password_confirmation")) {
	  error = "Die beiden Passwörter stimmen nicht überein.";
	  return;
	}
	busy = true;
	error = "";
	try {
	  await api("/api/v1/auth/signup", {
		method: "POST",
		body: JSON.stringify({ email: form.get("email"), display_name: form.get("display_name"), password: form.get("password") })
	  });
	  notice = "Wir haben dir einen Bestätigungslink geschickt. Danach kannst du das erste Kinderprofil anlegen.";
	  authMode = "login";
	} catch (requestError) {
	  error = requestError.message;
	} finally {
	  busy = false;
	}
  }

  async function verifySignupToken() {
	phase = "loading";
	error = "";
	try {
	  sessionEnvelope = await api("/api/v1/auth/verify-email", { method: "POST", body: JSON.stringify({ token: authToken }) });
	  setCsrf(sessionEnvelope.csrf_token);
	  window.history.replaceState({}, "", "/");
	  authToken = "";
	  await loadProfiles();
	  await loadCommerce();
	  await loadOverview();
	  phase = "adult";
	  notice = "Dein Elternkonto ist bestätigt. Lege jetzt das erste Kinderprofil an.";
	  if (profiles.length === 0) askToEditProfile(null);
	} catch (requestError) {
	  error = requestError.message;
	  authMode = "login";
	  phase = "login";
	}
  }

  function askToEditProfile(profile) {
	restoreFocus = document.activeElement;
	editingProfile = profile ? { ...profile } : { display_name: "", grade: 3, avatar_key: "characters/philip", revision: 0 };
	error = "";
	profileDialogError = "";
	setTimeout(() => document.getElementById("profile-name")?.focus());
  }

  function closeProfileDialog() {
	if (profileSavePending) return;
	editingProfile = null;
	profileDialogError = "";
	setTimeout(() => restoreFocus?.focus?.());
  }

  function handleProfileDialogKeydown(event) {
	if (event.key === "Escape") {
	  event.preventDefault();
	  closeProfileDialog();
	  return;
	}
	trapDialogFocus(event, profileDialog);
  }

  async function saveProfile(event) {
	const form = new FormData(event.currentTarget);
	const existingID = editingProfile?.id;
	let writeCompleted = false;
	profileSavePending = true;
	busy = true;
	error = "";
	profileDialogError = "";
	try {
	  await api(existingID ? "/api/v1/parent/profiles/" + encodeURIComponent(existingID) : "/api/v1/parent/profiles", {
		method: existingID ? "PATCH" : "POST",
		body: JSON.stringify({
		  display_name: form.get("display_name"),
		  grade: Number(form.get("grade")),
		  avatar_key: form.get("avatar_key"),
		  ...(existingID ? { expected_revision: Number(editingProfile.revision) } : {})
		})
	  });
	  writeCompleted = true;
	  await loadProfiles();
	  await loadOverview();
	  editingProfile = null;
	  notice = existingID ? "Das Profil wurde aktualisiert." : "Das Kinderprofil ist bereit.";
	} catch (requestError) {
	  if (writeCompleted) {
		editingProfile = null;
		error = "Das Profil wurde gespeichert, aber die Ansicht konnte nicht aktualisiert werden. Bitte lade die Seite neu.";
	  } else {
		profileDialogError = requestError.message;
	  }
	} finally {
	  profileSavePending = false;
	  busy = false;
	}
  }

  async function requestReset(event) {
    busy = true;
    error = "";
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/v1/auth/request-password-reset", {
        method: "POST",
        body: JSON.stringify({ email: form.get("email") })
      });
      notice = "Wenn ein Konto existiert, ist die Nachricht unterwegs.";
      authMode = "login";
    } catch (requestError) {
      error = requestError.message;
    } finally {
      busy = false;
    }
  }

  async function acceptInvitation(event) {
    const form = new FormData(event.currentTarget);
    if (form.get("password") !== form.get("password_confirmation")) {
      error = "Die beiden Passwörter stimmen nicht überein.";
      return;
    }
    busy = true;
    error = "";
    try {
      sessionEnvelope = await api("/api/v1/auth/accept-invitation", {
        method: "POST",
        body: JSON.stringify({
          token: authToken,
          display_name: form.get("display_name"),
          password: form.get("password")
        })
      });
      setCsrf(sessionEnvelope.csrf_token);
      window.history.replaceState({}, "", "/");
      await loadProfiles();
      await loadCommerce();
      await loadOverview();
      phase = "adult";
    } catch (requestError) {
      error = requestError.message;
    } finally {
      busy = false;
    }
  }

  async function completeReset(event) {
    const form = new FormData(event.currentTarget);
    if (form.get("new_password") !== form.get("password_confirmation")) {
      error = "Die beiden Passwörter stimmen nicht überein.";
      return;
    }
    busy = true;
    error = "";
    try {
      await api("/api/v1/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token: authToken, new_password: form.get("new_password") })
      });
      window.history.replaceState({}, "", "/");
      authToken = "";
      authMode = "login";
      notice = "Dein Passwort wurde geändert. Du kannst dich jetzt anmelden.";
    } catch (requestError) {
      error = requestError.message;
    } finally {
      busy = false;
    }
  }

  async function revokeDevice(device) {
    busy = true;
    error = "";
    try {
      await api("/api/v1/parent/devices/" + encodeURIComponent(device.session_id), { method: "DELETE" });
      devices = devices.filter((item) => item.session_id !== device.session_id);
      if (overview) overview = { ...overview, remembered_child_devices: devices.length };
      notice = "Der Kinderzugang wurde von diesem Gerät entfernt.";
    } catch (requestError) {
      error = requestError.message;
    } finally {
      busy = false;
    }
  }

  async function exportProfile(profile) {
    busy = true;
    error = "";
    try {
      await downloadProfileExport(profile);
      notice = "Der Export für " + profile.display_name + " wurde heruntergeladen.";
    } catch (requestError) {
      error = requestError.message;
    } finally {
      busy = false;
    }
  }

  function askToDelete(profile) {
    restoreFocus = document.activeElement;
    deletingProfile = profile;
    deleteConfirmation = "";
	deletePassword = "";
    error = "";
    deleteDialogError = "";
    setTimeout(() => document.getElementById("delete-confirmation")?.focus());
  }

  function closeDeleteDialog() {
	if (profileDeletePending) return;
    deletingProfile = null;
    deleteConfirmation = "";
	deletePassword = "";
    deleteDialogError = "";
    setTimeout(() => restoreFocus?.focus?.());
  }

  function handleDeleteDialogKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeDeleteDialog();
      return;
    }
	trapDialogFocus(event, deleteDialog);
  }

  function trapDialogFocus(event, dialog) {
	if (event.key !== "Tab" || !dialog) return;
	const focusable = [...dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href]')];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
	if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function askToDeleteAccount() {
	restoreFocus = document.activeElement;
	deletingAccount = true;
	accountDeleteConfirmation = "";
	accountDeletePassword = "";
	error = "";
	accountDeleteDialogError = "";
	setTimeout(() => document.getElementById("account-delete-confirmation")?.focus());
  }

  function closeAccountDeleteDialog() {
	if (accountDeletePending) return;
	deletingAccount = false;
	accountDeleteConfirmation = "";
	accountDeletePassword = "";
	accountDeleteDialogError = "";
	setTimeout(() => restoreFocus?.focus?.());
  }

  function handleAccountDeleteKeydown(event) {
	if (event.key === "Escape") {
	  event.preventDefault();
	  closeAccountDeleteDialog();
	  return;
	}
	trapDialogFocus(event, accountDeleteDialog);
  }

  async function confirmAccountDelete() {
	if (accountDeleteConfirmation !== "KONTO LÖSCHEN" || !accountDeletePassword) return;
	accountDeletePending = true;
	busy = true;
	error = "";
	accountDeleteDialogError = "";
	try {
	  const householdID = sessionEnvelope?.household?.id;
	  const grant = await api("/api/v1/parent/reauth", {
		method: "POST",
		body: JSON.stringify({ password: accountDeletePassword, action: "delete_account", target_id: householdID })
	  });
	  await api("/api/v1/parent/account", { method: "DELETE", body: JSON.stringify({ capability: grant.capability }) });
	  deletingAccount = false;
	  sessionEnvelope = null;
	  profiles = [];
	  overview = null;
	  devices = [];
	  learningBridge?.deactivate();
	  setCsrf("");
	  phase = "login";
	  notice = "Das Familienkonto wurde gelöscht.";
	} catch (requestError) {
	  accountDeleteDialogError = requestError.message;
	} finally {
	  accountDeletePending = false;
	  busy = false;
	}
  }

  async function confirmDelete() {
	if (!deletingProfile || deleteConfirmation.trim() !== deletingProfile.display_name || !deletePassword) return;
	let deletionCompleted = false;
	profileDeletePending = true;
    busy = true;
    error = "";
    deleteDialogError = "";
    try {
	  const grant = await api("/api/v1/parent/reauth", {
		method: "POST",
		body: JSON.stringify({ password: deletePassword, action: "delete_profile", target_id: deletingProfile.id })
	  });
      await api("/api/v1/parent/profiles/" + encodeURIComponent(deletingProfile.id), {
        method: "DELETE",
		body: JSON.stringify({ exact_name: deleteConfirmation.trim(), capability: grant.capability })
	  });
	  deletionCompleted = true;
      const removedName = deletingProfile.display_name;
      deletingProfile = null;
      deleteConfirmation = "";
	  deletePassword = "";
      await loadProfiles();
      await loadOverview();
      notice = "Das Profil " + removedName + " und seine Lerndaten wurden gelöscht.";
    } catch (requestError) {
	  if (deletionCompleted) {
		deletingProfile = null;
		error = "Das Profil wurde gelöscht, aber die Ansicht konnte nicht aktualisiert werden. Bitte lade die Seite neu.";
	  } else {
		deleteDialogError = requestError.message;
	  }
    } finally {
	  profileDeletePending = false;
      busy = false;
    }
  }

  const deviceName = (device) => device.label || "Kindergerät";
  const formatDate = (value) => new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));

  async function startChildMode() {
    busy = true;
    error = "";
    try {
      sessionEnvelope = await api("/api/v1/child-mode", {
        method: "POST",
        body: JSON.stringify({ allowed_profile_ids: profiles.map((profile) => profile.id) })
      });
      setCsrf(sessionEnvelope.csrf_token);
      overview = null;
      phase = "child";
    } catch (requestError) {
      error = requestError.message;
    } finally {
      busy = false;
    }
  }

  async function logout() {
    busy = true;
    activationGeneration += 1;
    clearGameTimers();
    try {
      await api("/api/v1/auth/logout", { method: "POST" });
    } catch {
      // The local transition is safe even when the server session expired.
    }
    sessionEnvelope = null;
    profiles = [];
    devices = [];
    learningBridge?.deactivate();
    selectedProfile = null;
    gameInstanceUrl = "";
    setCsrf("");
    phase = "login";
    busy = false;
  }

  async function openProfile(profile) {
    if (activatingProfile) return;
    activatingProfile = true;
    const generation = ++activationGeneration;
    error = "";
    try {
      if (!gameAvailable) throw new Error("Die Lernmission ist gerade nicht verfügbar.");
      const activeEpoch = await learningBridge?.activate(profile);
      if (generation !== activationGeneration) return;
      clearGameTimers();
      gameStatus = "loading";
      loaderProgress = 0;
      gameInstanceUrl = `${gameBuildUrl}#epoch=${activeEpoch}`;
      selectedProfile = profile;
      loaderTimer = window.setTimeout(() => { gameStatus = "loader-timeout"; }, 45_000);
    } catch (bridgeError) {
      error = bridgeError.message;
      gameStatus = bridgeError?.code === "tab_conflict" ? "conflict" : "unavailable";
    } finally {
      if (generation === activationGeneration) activatingProfile = false;
    }
  }

  async function retryLearning() {
    const profile = selectedProfile;
    if (!profile) return;
    activationGeneration += 1;
    clearGameTimers();
    learningBridge?.deactivate();
    selectedProfile = null;
    await discoverGameBuild();
    if (gameAvailable) await openProfile(profile);
  }

  async function closeProfile() {
    activationGeneration += 1;
    clearGameTimers();
    await learningBridge?.prepareClose();
    learningBridge?.deactivate();
    selectedProfile = null;
    gameInstanceUrl = "";
    gameStatus = gameAvailable ? "available" : "unavailable";
    loadProfiles().catch(() => {});
  }
</script>

<svelte:head>
  <title>{publicPage === "datenschutz" ? "Datenschutz · Wissensbande" : publicPage === "impressum" ? "Impressum · Wissensbande" : publicPage === "support" ? "Hilfe · Wissensbande" : phase === "login" ? "Anmelden · Wissensbande" : "Wissensbande"}</title>
  <meta name="robots" content="noindex, nofollow, noarchive" />
</svelte:head>

{#if publicPage}
  <div class="legal-shell">
    <header class="legal-topbar">
      <a href={publicReturnHref()} aria-label="Zurück zur Wissensbande"><img src="/brand/logo.svg" alt="Wissensbande" /></a>
      <a class="quiet-button" href={publicReturnHref()}>← Zur Anmeldung</a>
    </header>
    <main class="legal-card">
      {#if publicPage === "impressum"}
        <span class="eyebrow">RECHTLICHE INFORMATIONEN</span>
        <h1>Impressum</h1>
        <p class="legal-lead">Anbieterkennzeichnung gemäß § 5 Digitale-Dienste-Gesetz (DDG).</p>

        <section>
          <h2>Anbieter</h2>
          <p>Momentum Advisory GmbH<br />Kieler Str. 72<br />24119 Kronshagen<br />Deutschland</p>
        </section>
		<section>
		  <h2>Vertretung und Kontakt</h2>
		  <p>Vertreten durch den Geschäftsführer Heinrich Ruhwasser.</p>
		  {#if externalContactLinksAllowed()}
			<p>Telefon: <a href="tel:+4943155606200">+49 431 556062-00</a><br />E-Mail: <a href="mailto:info@momentumadvisory.de">info@momentumadvisory.de</a></p>
		  {:else}
			<p>Telefon: +49 431 556062-00<br />E-Mail: info@momentumadvisory.de</p>
		  {/if}
        </section>
        <section>
          <h2>Register und Umsatzsteuer</h2>
          <p>Registergericht: Amtsgericht Kiel<br />Handelsregisternummer: HRB 25878</p>
          <p>Umsatzsteuer-Identifikationsnummer gemäß § 27a Umsatzsteuergesetz: DE363387438</p>
        </section>
        <section>
          <h2>Redaktionell verantwortlich</h2>
          <p>Heinrich Ruhwasser, c/o Momentum Advisory GmbH, Kieler Str. 72, 24119 Kronshagen.</p>
        </section>
      {:else if publicPage === "support"}
        <span class="eyebrow">HILFE FÜR ELTERN</span>
        <h1>Wissensbande Support</h1>
        <p class="legal-lead">Wir helfen bei Anmeldung, Kinderprofilen, Lernständen und der Wiederherstellung eines Apple-Kaufs.</p>
		<section>
		  <h2>Kontakt</h2>
		  {#if externalContactLinksAllowed()}
			<p>Schreibe uns an <a href="mailto:info@momentumadvisory.de">info@momentumadvisory.de</a> oder rufe unter <a href="tel:+4943155606200">+49 431 556062-00</a> an.</p>
		  {:else}
			<p>Schreibe uns an info@momentumadvisory.de oder rufe unter +49 431 556062-00 an. Öffne diese Kontaktdaten im geschützten Elternbereich, um E-Mail oder Telefon direkt zu starten.</p>
		  {/if}
          <p>Bitte sende keine Passwörter und keine Lernantworten eines Kindes. Für eine Kaufprüfung genügen die E-Mail-Adresse des Elternkontos und eine kurze Fehlerbeschreibung.</p>
        </section>
        <section>
          <h2>Apple-Kauf wiederherstellen</h2>
          <p>Öffne in der iPad-App den geschützten Elternbereich, wähle „Wissenspaket“ und dann „Käufe wiederherstellen“. Die App prüft den signierten Apple-Beleg erneut; sie schaltet niemals allein aufgrund einer lokalen Erfolgsmeldung frei.</p>
        </section>
      {:else}
        <span class="eyebrow">DEINE DATEN. KLAR ERKLÄRT.</span>
        <h1>Datenschutz</h1>
        <p class="legal-lead">Diese Hinweise erklären, welche Daten Wissensbande benötigt, warum sie verarbeitet werden und welche Rechte Familien haben.</p>

		<section>
		  <h2>1. Verantwortlicher und Kontakt</h2>
		  <p>Momentum Advisory GmbH, Kieler Str. 72, 24119 Kronshagen, Deutschland.</p>
		  {#if externalContactLinksAllowed()}
			<p>Datenschutzanfragen: <a href="mailto:datenschutz@momentumadvisory.de">datenschutz@momentumadvisory.de</a><br />Allgemeiner Kontakt: <a href="mailto:info@momentumadvisory.de">info@momentumadvisory.de</a></p>
		  {:else}
			<p>Datenschutzanfragen: datenschutz@momentumadvisory.de<br />Allgemeiner Kontakt: info@momentumadvisory.de</p>
		  {/if}
        </section>
        <section>
          <h2>2. Welche Daten wir verarbeiten</h2>
          <p>Für den geschützten Elternzugang verarbeiten wir insbesondere Anzeigename, E-Mail-Adresse, Passwort-Hash sowie notwendige Anmelde- und Sicherheitsinformationen. Für Kinderprofile verarbeiten wir Anzeigename, Klassenstufe, Avatar, Aufgabenfortschritt, Antworten und Bewertungen, pausierte Spielstände, Sterne, Medaillen und Charakterfortschritt. Bei einem Apple-Kauf verarbeiten wir außerdem eine pseudonyme Kontozuordnung, Produkt- und Transaktionskennungen, Kauf-, Signatur- und gegebenenfalls Widerrufszeitpunkte sowie vorübergehend den signierten Apple-Beleg.</p>
          <p>Wissensbande nutzt keine Werbung und kein Marketing- oder Verhaltensprofiling. Mikrofon, Kamera, Fotos sowie Spracheingabe oder Sprachausgabe werden nicht verwendet.</p>
        </section>
        <section>
          <h2>3. Zwecke und Rechtsgrundlagen</h2>
          <p>Wir verwenden die Daten, um den Elternzugang bereitzustellen, Kinderprofile zuzuordnen, Lernstände geräteübergreifend zu speichern, pausierte Missionen fortzusetzen, Erfolge anzuzeigen, Passwörter zurückzusetzen sowie den Dienst sicher und zuverlässig zu betreiben.</p>
          <p>Die Verarbeitung zur Bereitstellung der von Eltern gewählten Funktionen erfolgt auf Grundlage von Art. 6 Abs. 1 Buchst. b DSGVO. Gesetzlich erforderliche Verarbeitung beruht auf Art. 6 Abs. 1 Buchst. c DSGVO. Sicherheits-, Missbrauchsschutz- und Fehlerdiagnosedaten verarbeiten wir auf Grundlage unseres berechtigten Interesses an einem sicheren Dienst gemäß Art. 6 Abs. 1 Buchst. f DSGVO.</p>
        </section>
        <section>
          <h2>4. Cookies und lokale Speicherung</h2>
          <p>Wir verwenden ausschließlich technisch notwendige Sitzungs- und Sicherheitsinformationen. Sie ermöglichen Anmeldung, Schutz vor missbräuchlichen Anfragen und die Fortsetzung einer Sitzung. Während der Nutzung im Browser speichert die Lernanwendung außerdem den Spielstand und noch nicht übertragene Änderungen lokal in einem dem Haushalt und Kinderprofil zugeordneten Browser-Speicher. So kann eine bereits geöffnete Mission eine kurze Unterbrechung überbrücken und später synchronisieren; ein erneutes Öffnen der Seite ohne Internetverbindung wird nicht zugesagt.</p>
          <p>Optionale Analyse-, Werbe- oder Marketing-Cookies sowie ein installierbarer Offline-Cache werden nicht eingesetzt. Die technisch notwendige Speicherung erfolgt gemäß § 25 Abs. 2 Nr. 2 TDDDG. Lokale Website-Daten können über die Browser-Einstellungen gelöscht werden; noch nicht synchronisierte Änderungen gehen dabei verloren.</p>
        </section>
        <section>
          <h2>5. Empfänger</h2>
          <p>Daten erhalten nur Personen, die sie für Betrieb und Support benötigen, sowie sorgfältig ausgewählte technische Dienstleister im erforderlichen Umfang. Für Passwort- und Sicherheitsnachrichten nutzen wir einen beauftragten E-Mail-Zustelldienst. Bei In-App-Käufen verarbeitet Apple die Zahlung und übermittelt signierte Transaktions- und Widerrufsinformationen. Eine Weitergabe für Werbung findet nicht statt.</p>
        </section>
        <section>
          <h2>6. Speicherdauer</h2>
          <ul>
            <li>Fortsetzbare Aufgabenläufe werden bis zu 360 Tage gespeichert.</li>
            <li>Anmelde- und sicherheitsbezogene Aufzeichnungen werden regelmäßig nach 120 Tagen gelöscht.</li>
            <li>Erfolgreiche Nachweis- und Sicherheitsprotokolle werden bis zu 720 Tage gespeichert.</li>
            <li>Fehlgeschlagene oder sicherheitsrelevante Nachweise werden bis zu 1.460 Tage gespeichert.</li>
            <li>Nachweise über den Versand notwendiger E-Mails werden bis zu 360 Tage gespeichert.</li>
			<li>Rohe signierte Apple-Transaktionen werden nach 30 Tagen, rohe App-Store-Benachrichtigungen nach 120 Tagen gelöscht.</li>
			<li>Aktive Kaufzuordnungen bleiben für die Wiederherstellung erhalten. Widerrufene, erstattete oder nicht mehr unterstützte Zuordnungsnachweise werden drei Jahre nach dem maßgeblichen Ereignis gelöscht, sofern keine gesetzliche Pflicht entgegensteht.</li>
          </ul>
          <p>Konten, Profile und zugehörige Lernstände speichern wir, solange sie für die Nutzung benötigt werden. Eltern können Profildaten exportieren und Profile im geschützten Elternbereich löschen. Gesetzliche Aufbewahrungspflichten bleiben unberührt.</p>
        </section>
		<section>
		  <h2>7. Rechte</h2>
		  <p>Betroffene Personen haben nach Maßgabe der DSGVO das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch. Erteilte Einwilligungen können mit Wirkung für die Zukunft widerrufen werden. Außerdem besteht ein Beschwerderecht bei einer Datenschutzaufsichtsbehörde.</p>
		  {#if externalContactLinksAllowed()}
			<p>Zur Ausübung dieser Rechte genügt eine Nachricht an <a href="mailto:datenschutz@momentumadvisory.de">datenschutz@momentumadvisory.de</a>.</p>
		  {:else}
			<p>Zur Ausübung dieser Rechte genügt eine Nachricht an datenschutz@momentumadvisory.de.</p>
		  {/if}
        </section>
        <section>
          <h2>8. Sicherheit und Aktualisierung</h2>
          <p>Wir setzen angemessene technische und organisatorische Maßnahmen ein, um personenbezogene Daten zu schützen. Diese Hinweise werden angepasst, wenn sich Funktionen oder rechtliche Anforderungen ändern.</p>
          <p>Stand: 9. September 2026</p>
        </section>
      {/if}
      <nav class="legal-links" aria-label="Rechtliche Seiten">
        <a href={legalHref("/datenschutz")}>Datenschutz</a>
        <a href={legalHref("/impressum")}>Impressum</a>
		<a href={legalHref("/support")}>Hilfe</a>
      </nav>
    </main>
  </div>
{:else if selectedProfile}
  <section class="learning-shell" aria-label={"Lernmission für " + selectedProfile.display_name}>
    <header>
      <button class="quiet-button" onclick={closeProfile}>← Profile</button>
      <div>
        <span class="eyebrow">MISSION BEREIT</span>
        <strong>{selectedProfile.display_name}</strong>
      </div>
      <div class="star-chip">★ {selectedProfile.summary.total_stars}</div>
    </header>
    <div class="game-stage">
      {#if gameAvailable && gameInstanceUrl}
        <iframe
          bind:this={gameFrame}
          title={"Wissensbande für " + selectedProfile.display_name}
          src={gameInstanceUrl}
          allow="fullscreen"
          use:gateGameFrame={gameStatus}
        ></iframe>
      {/if}
      {#if gameStatus !== "ready"}
        <div class="game-fallback">
          <span class="orb">✦</span>
          <h2>{gameStatus === "conflict" ? "Mission schon geöffnet" : gameStatus === "loading" ? "Mission wird geladen" : gameStatus === "initializing" ? "Spielstand wird abgeglichen" : "Mission gerade nicht verfügbar"}</h2>
          <p>{gameStatus === "conflict" ? "Schließe die Mission im anderen Browser-Tab und versuche es erneut." : gameStatus === "initializing" ? "Deine lokalen und gespeicherten Fortschritte werden sicher zusammengeführt." : "Bitte prüfe deine Verbindung und versuche es gleich noch einmal."}</p>
          {#if gameStatus === "loading" && loaderProgress > 0}<progress max="100" value={loaderProgress} aria-label="Ladefortschritt">{loaderProgress}%</progress>{/if}
          {#if !["loading", "initializing"].includes(gameStatus)}<button class="primary-button compact" onclick={retryLearning}>Noch einmal versuchen</button>{/if}
        </div>
      {/if}
    </div>
  </section>
{:else if phase === "loading"}
  <main class="center-stage">
    <div class="loader" aria-label="Wissensbande wird geladen"></div>
    <p>Die Bande kommt zusammen …</p>
  </main>
{:else if phase === "login"}
  <main class="login-layout" class:oauth-compact={Boolean(oauthReturnPath())}>
    <section class="brand-panel">
      <img class="brand-logo" src="/brand/logo.svg" alt="Wissensbande" />
      <div class="brand-copy">
        <span class="eyebrow light">LERNEN. LEVELN. LOSLEGEN.</span>
        <h1>Willkommen bei der Wissensbande.</h1>
        <p>Mathe, Deutsch, Englisch – spannende Missionen, eigene Erfolge und immer genau da weitermachen, wo ihr aufgehört habt.</p>
      </div>
      <div class="crew-preview" aria-hidden="true">
        <img src="/characters/johanna.svg" alt="" />
        <img src="/characters/philip.svg" alt="" />
      </div>
    </section>
    <section class="login-card">
      <div class="login-heading">
        <span class="mini-mark">WB</span>
        <div>
          <span class="eyebrow">ELTERNBEREICH</span>
          <h2>{authMode === "reset-request" ? "Passwort-Hilfe" : authMode === "reset-complete" ? "Neues Passwort" : authMode === "invitation" ? "Einladung annehmen" : authMode === "signup" ? "Elternkonto anlegen" : "Sicher anmelden"}</h2>
        </div>
      </div>
      {#if notice}<p class="notice" role="status">{notice}</p>{/if}
      {#if error}<p class="error" role="alert">{error}</p>{/if}
      {#if authMode === "reset-request"}
        <form onsubmit={(event) => { event.preventDefault(); requestReset(event); }}>
          <label for="reset-email">E-Mail-Adresse</label>
          <input id="reset-email" name="email" type="email" autocomplete="email" required />
          <button class="primary-button" disabled={busy}>{busy ? "Einen Moment …" : "Link anfordern"}</button>
          <button type="button" class="text-button" onclick={() => { authMode = "login"; error = ""; }}>Zurück zur Anmeldung</button>
        </form>
      {:else if authMode === "reset-complete"}
        <form onsubmit={(event) => { event.preventDefault(); completeReset(event); }}>
          <p class="form-intro">Wähle ein neues Passwort mit mindestens 12 Zeichen.</p>
          <label for="new-password">Neues Passwort</label>
          <input id="new-password" name="new_password" type="password" minlength="12" autocomplete="new-password" required />
          <label for="new-password-confirmation">Passwort wiederholen</label>
          <input id="new-password-confirmation" name="password_confirmation" type="password" minlength="12" autocomplete="new-password" required />
          <button class="primary-button" disabled={busy}>{busy ? "Wird gespeichert …" : "Passwort speichern"}</button>
        </form>
      {:else if authMode === "invitation"}
        <form onsubmit={(event) => { event.preventDefault(); acceptInvitation(event); }}>
          <p class="form-intro">Richte deinen geschützten Elternzugang ein.</p>
          <label for="invite-name">Dein Name</label>
          <input id="invite-name" name="display_name" type="text" maxlength="100" autocomplete="name" required />
          <label for="invite-password">Passwort</label>
          <input id="invite-password" name="password" type="password" minlength="12" autocomplete="new-password" required />
          <label for="invite-password-confirmation">Passwort wiederholen</label>
          <input id="invite-password-confirmation" name="password_confirmation" type="password" minlength="12" autocomplete="new-password" required />
          <button class="primary-button" disabled={busy}>{busy ? "Wird eingerichtet …" : "Zugang einrichten"}</button>
        </form>
	  {:else if authMode === "signup"}
		<form onsubmit={(event) => { event.preventDefault(); signup(event); }}>
		  <p class="form-intro">Eltern richten das Familienkonto ein. Kinder brauchen keine E-Mail-Adresse.</p>
		  <label for="signup-name">Dein Name</label>
		  <input id="signup-name" name="display_name" type="text" maxlength="100" autocomplete="name" required />
		  <label for="signup-email">E-Mail-Adresse</label>
		  <input id="signup-email" name="email" type="email" autocomplete="email" required />
		  <label for="signup-password">Passwort (mindestens 12 Zeichen)</label>
		  <input id="signup-password" name="password" type="password" minlength="12" autocomplete="new-password" required />
		  <label for="signup-password-confirmation">Passwort wiederholen</label>
		  <input id="signup-password-confirmation" name="password_confirmation" type="password" minlength="12" autocomplete="new-password" required />
		  <button class="primary-button" disabled={busy}>{busy ? "Wird eingerichtet …" : "Konto anlegen"}</button>
		  <button type="button" class="text-button" onclick={() => { authMode = "login"; error = ""; }}>Schon ein Konto? Anmelden</button>
		</form>
      {:else}
        <form onsubmit={(event) => { event.preventDefault(); login(event); }}>
          <label for="email">E-Mail-Adresse</label>
          <input id="email" name="email" type="email" autocomplete="username" required />
          <label for="password">Passwort</label>
          <input id="password" name="password" type="password" autocomplete="current-password" required />
          <button class="primary-button" disabled={busy}>{busy ? "Wird geprüft …" : "Anmelden"}</button>
          <button type="button" class="text-button" onclick={() => { authMode = "reset-request"; error = ""; notice = ""; }}>Passwort vergessen?</button>
		  <button type="button" class="text-button" onclick={() => { authMode = "signup"; error = ""; notice = ""; }}>Noch kein Konto? Kostenlos starten</button>
        </form>
      {/if}
      <p class="privacy-note">Kinder brauchen keine eigenen Zugangsdaten. Die Eltern-Anmeldung schützt Einstellungen und Daten.</p>
      <nav class="legal-links" aria-label="Rechtliche Seiten">
        <a href={legalHref("/datenschutz")}>Datenschutz</a>
        <a href={legalHref("/impressum")}>Impressum</a>
		<a href={legalHref("/support")}>Hilfe</a>
      </nav>
    </section>
  </main>
{:else if phase === "offline"}
  <main class="center-stage">
    <span class="large-icon">☁</span>
    <h1>Gerade keine Verbindung</h1>
    <p>{error}</p>
    <button class="primary-button compact" onclick={bootstrap}>Noch einmal versuchen</button>
  </main>
{:else}
  <main class="app-shell" inert={Boolean(deletingProfile || editingProfile || deletingAccount)}>
    <header class="topbar">
      <img src="/brand/logo.svg" alt="Wissensbande" />
      <div class="top-actions">
        <span class:online={isOnline} class="connection-dot"></span>
        <span class="connection-label">{syncStatus === "up_to_date" ? "synchron" : syncStatus === "syncing" ? "wird gesichert" : syncStatus === "storage_error" ? "Speicherfehler" : syncStatus === "authentication_required" ? "Anmeldung erneuern" : isOnline ? "lokal bereit" : "wartet auf Verbindung"}</span>
        {#if phase === "adult"}
          <button class="quiet-button" onclick={logout} disabled={busy}>Abmelden</button>
        {:else}
          <button class="quiet-button" onclick={logout} disabled={activatingProfile}>Eltern-Anmeldung</button>
        {/if}
      </div>
    </header>

    <section class="hero">
      <div>
        <span class="eyebrow">{phase === "adult" ? "ELTERNBEREICH" : "WER SPIELT HEUTE?"}</span>
        <h1>{phase === "adult" ? "Hallo, " + sessionEnvelope.session.user.display_name + "!" : "Wählt euer Profil."}</h1>
        <p>{phase === "adult" ? "Hier seht ihr Fortschritt und startet den sicheren Kinderbereich." : "Eure Sterne, Level und pausierten Missionen warten schon."}</p>
      </div>
      {#if phase === "adult"}
        <button class="child-mode-button" onclick={startChildMode} disabled={busy || profiles.length === 0}>
          <span>✦</span>
          <span><strong>Kinderbereich starten</strong><small>Auf diesem Gerät merken</small></span>
          <b>→</b>
        </button>
      {/if}
    </section>

    {#if error}<p class="error wide" role="alert">{error}</p>{/if}
    {#if notice}<p class="notice wide" role="status">{notice}</p>{/if}

    <section class="profile-grid" aria-label="Profile">
      {#each profiles as profile}
        <button
          class="profile-card"
          style={"--accent:" + profileColour(profile.profile_key)}
          onclick={() => openProfile(profile)}
          disabled={busy || activatingProfile}
        >
          <div class="avatar-wrap">
            <img src={avatar(profile)} alt="" />
            <span class="grade">{profile.grade}. Klasse</span>
          </div>
          <div class="profile-info">
            <span class="eyebrow">DEIN PROFIL</span>
            <h2>{profile.display_name}</h2>
            <div class="reward-row">
              <span class="stars">★ {profile.summary.total_stars}</span>
              <span>{rankName(profile.summary.character_level_id)}</span>
            </div>
            <span class="mission-link">Missionen öffnen <b>→</b></span>
          </div>
        </button>
      {/each}
	  {#if profiles.length === 0}
		<p class="empty-state wide">Lege im Elternbereich zuerst ein Kinderprofil an. Danach kann der Kinderbereich gestartet werden.</p>
	  {/if}
    </section>

    {#if phase === "adult" && overview}
      <section class="parent-summary">
        <div>
          <span class="eyebrow">ÜBERBLICK</span>
          <h2>Alles im Blick, ohne Lernspaß zu stören.</h2>
        </div>
        <div class="summary-stat"><strong>{overview.profiles.length}</strong><span>Kinderprofile</span></div>
        <div class="summary-stat"><strong>{overview.remembered_child_devices}</strong><span>gemerkte Geräte</span></div>
        <div class="summary-stat"><strong>{overview.member_count}</strong><span>Elternzugang</span></div>
      </section>

      <section class="parent-tools" aria-labelledby="parent-tools-title">
        <div class="section-heading">
          <div>
            <span class="eyebrow">VERWALTUNG</span>
			<h2 id="parent-tools-title">Profile, Geräte und Lerndaten</h2>
          </div>
          <p>Geschützte Werkzeuge nur für Erwachsene.</p>
        </div>

        <div class="management-grid">
		  <article class="management-card">
			<div class="card-title-row">
			  <span class="tool-icon violet" aria-hidden="true">+</span>
			  <div><h3>Kinderprofile</h3><p>Spitzname, Klassenstufe und Figur verwalten.</p></div>
			</div>
			<button class="secondary-button full-width" onclick={() => askToEditProfile(null)} disabled={busy || profiles.length >= 8}>Neues Profil anlegen</button>
			<ul class="profile-tools-list">
			  {#each profiles as profile}
				<li>
				  <div><strong>{profile.display_name}</strong><small>{profile.grade}. Klasse</small></div>
				  <button class="secondary-button" onclick={() => askToEditProfile(profile)} disabled={busy}>Bearbeiten</button>
				</li>
			  {/each}
			</ul>
		  </article>
          <article class="management-card">
            <div class="card-title-row">
              <span class="tool-icon" aria-hidden="true">▣</span>
              <div><h3>Gemerkte Kindergeräte</h3><p>Zugänge prüfen oder einzeln beenden.</p></div>
            </div>
            {#if devices.length === 0}
              <p class="empty-state">Noch kein Kindergerät dauerhaft angemeldet.</p>
            {:else}
              <ul class="device-list">
                {#each devices as device}
                  <li>
                    <div>
                      <strong>{deviceName(device)}</strong>
                      <small>Zuletzt aktiv: {formatDate(device.last_seen_at)}</small>
                    </div>
                    <button class="danger-button subtle" onclick={() => revokeDevice(device)} disabled={busy}>Zugang beenden</button>
                  </li>
                {/each}
              </ul>
            {/if}
          </article>

          <article class="management-card">
            <div class="card-title-row">
              <span class="tool-icon violet" aria-hidden="true">⇩</span>
              <div><h3>Profildaten</h3><p>Eine Kopie laden oder ein Profil löschen.</p></div>
            </div>
            <ul class="profile-tools-list">
              {#each profiles as profile}
                <li>
                  <div><strong>{profile.display_name}</strong><small>{profile.grade}. Klasse · {profile.summary.total_stars} Sterne</small></div>
                  <div class="row-actions">
                    <button class="secondary-button" onclick={() => exportProfile(profile)} disabled={busy}>Exportieren</button>
                    <button class="danger-button" onclick={() => askToDelete(profile)} disabled={busy}>Löschen</button>
                  </div>
                </li>
              {/each}
            </ul>
          </article>
          <article class="management-card danger-zone">
            <div class="card-title-row">
              <span class="tool-icon danger" aria-hidden="true">!</span>
              <div><h3>Familienkonto</h3><p>Alle Profile, Lernstände und Zugänge gemeinsam löschen.</p></div>
            </div>
            <p class="empty-state">Kaufnachweise, die für eine sichere Wiederherstellung oder gesetzlich erforderlich sind, folgen den veröffentlichten Aufbewahrungsfristen.</p>
            <button class="danger-button full-width" onclick={askToDeleteAccount} disabled={busy}>Familienkonto löschen</button>
          </article>
        </div>
      </section>
    {/if}
    <nav class="legal-links app-legal-links" aria-label="Rechtliche Seiten">
      <a href={legalHref("/datenschutz")}>Datenschutz</a>
      <a href={legalHref("/impressum")}>Impressum</a>
	  <a href={legalHref("/support")}>Hilfe</a>
    </nav>
  </main>
{/if}

{#if deletingProfile}
  <div class="modal-backdrop" role="presentation" onclick={(event) => { if (event.target === event.currentTarget) closeDeleteDialog(); }}>
    <div bind:this={deleteDialog} class="confirmation-dialog" role="dialog" aria-modal="true" aria-busy={profileDeletePending} aria-labelledby="delete-title" tabindex="-1" onkeydown={handleDeleteDialogKeydown}>
      <span class="danger-mark" aria-hidden="true">!</span>
	  <h2 id="delete-title">Profil wirklich löschen?</h2>
	  <p>Alle Cloud-Lerndaten von <strong>{deletingProfile.display_name}</strong> werden dauerhaft gelöscht. Lade bei Bedarf vorher einen Export herunter.</p>
	  {#if deleteDialogError}<p class="error dialog-error" role="alert">{deleteDialogError}</p>{/if}
	  <label for="delete-confirmation">Tippe zur Bestätigung <strong>{deletingProfile.display_name}</strong> ein:</label>
      <input id="delete-confirmation" bind:value={deleteConfirmation} autocomplete="off" />
	  <label for="delete-password">Bestätige mit deinem Eltern-Passwort:</label>
	  <input id="delete-password" type="password" bind:value={deletePassword} autocomplete="current-password" />
      <div class="dialog-actions">
        <button class="secondary-button" onclick={closeDeleteDialog} disabled={busy}>Abbrechen</button>
		<button class="danger-button solid" onclick={confirmDelete} disabled={busy || !deletePassword || deleteConfirmation.trim() !== deletingProfile.display_name}>Profil dauerhaft löschen</button>
      </div>
    </div>
  </div>
{/if}

{#if editingProfile}
  <div class="modal-backdrop" role="presentation" onclick={(event) => { if (event.target === event.currentTarget) closeProfileDialog(); }}>
	<div bind:this={profileDialog} class="confirmation-dialog" role="dialog" aria-modal="true" aria-busy={profileSavePending} aria-labelledby="profile-dialog-title" tabindex="-1" onkeydown={handleProfileDialogKeydown}>
	  <span class="mini-mark" aria-hidden="true">WB</span>
	  <h2 id="profile-dialog-title">{editingProfile.id ? "Profil bearbeiten" : "Kinderprofil anlegen"}</h2>
	  <p>Verwende einen Spitznamen. E-Mail-Adresse und Geburtsdatum sind nicht nötig.</p>
	  {#if editingProfile.id}
		<p class="form-intro">Ein Wechsel der Klassenstufe ändert die empfohlenen Themen. Bereits gesammelte Fortschritte und Sterne bleiben erhalten.</p>
	  {/if}
	  {#if profileDialogError}<p class="error dialog-error" role="alert">{profileDialogError}</p>{/if}
	  <form onsubmit={(event) => { event.preventDefault(); saveProfile(event); }}>
		<label for="profile-name">Spitzname</label>
		<input id="profile-name" name="display_name" value={editingProfile.display_name} maxlength="40" autocomplete="off" required />
		<label for="profile-grade">Klassenstufe</label>
		<select id="profile-grade" name="grade" value={editingProfile.grade}>
		  <option value="3">3. Klasse</option>
		  <option value="4">4. Klasse</option>
		</select>
		<fieldset>
		  <legend>Figur</legend>
		  <label class="radio-option"><input type="radio" name="avatar_key" value="characters/philip" checked={editingProfile.avatar_key === "characters/philip"} /> Philip</label>
		  <label class="radio-option"><input type="radio" name="avatar_key" value="characters/johanna" checked={editingProfile.avatar_key === "characters/johanna"} /> Johanna</label>
		</fieldset>
		<div class="dialog-actions">
		  <button type="button" class="secondary-button" onclick={closeProfileDialog} disabled={busy}>Abbrechen</button>
		  <button class="primary-button compact" disabled={busy}>{busy ? "Wird gespeichert …" : "Profil speichern"}</button>
		</div>
	  </form>
	</div>
  </div>
{/if}

{#if deletingAccount}
  <div class="modal-backdrop" role="presentation" onclick={(event) => { if (event.target === event.currentTarget) closeAccountDeleteDialog(); }}>
    <div bind:this={accountDeleteDialog} class="confirmation-dialog" role="dialog" aria-modal="true" aria-busy={accountDeletePending} aria-labelledby="account-delete-title" tabindex="-1" onkeydown={handleAccountDeleteKeydown}>
      <span class="danger-mark" aria-hidden="true">!</span>
	  <h2 id="account-delete-title">Familienkonto dauerhaft löschen?</h2>
	  <p>Alle Kinderprofile, Lernstände, Gerätezugänge und Kontodaten werden gelöscht. Gesetzlich oder zur sicheren Kaufwiederherstellung erforderliche Transaktionsnachweise folgen den veröffentlichten Aufbewahrungsfristen.</p>
	  {#if accountDeleteDialogError}<p class="error dialog-error" role="alert">{accountDeleteDialogError}</p>{/if}
	  <label for="account-delete-confirmation">Tippe zur Bestätigung <strong>KONTO LÖSCHEN</strong> ein:</label>
      <input id="account-delete-confirmation" bind:value={accountDeleteConfirmation} autocomplete="off" />
      <label for="account-delete-password">Bestätige mit deinem Eltern-Passwort:</label>
      <input id="account-delete-password" type="password" bind:value={accountDeletePassword} autocomplete="current-password" />
      <div class="dialog-actions">
        <button class="secondary-button" onclick={closeAccountDeleteDialog} disabled={busy}>Abbrechen</button>
        <button class="danger-button solid" onclick={confirmAccountDelete} disabled={busy || !accountDeletePassword || accountDeleteConfirmation !== "KONTO LÖSCHEN"}>Konto endgültig löschen</button>
      </div>
    </div>
  </div>
{/if}
